import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Users } from 'lucide-react';
import { GlassBadge } from '@/design-system';
import { cn } from '@/utils/cn';
import type { Team } from '@/types';

interface TeamMembersPopoverProps {
  team: Team;
  onClickManage: () => void;
}

const OFFSET = 8;
const MAX_PREVIEW = 5;

const TeamMembersPopover: React.FC<TeamMembersPopoverProps> = ({ team, onClickManage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();

  const members = team.activeMembers || [];
  const memberCount = team.activeResourceCount ?? team.memberCount;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();

    let x = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
    let y = triggerRect.bottom + OFFSET;

    // Keep in viewport
    const padding = 8;
    x = Math.max(padding, Math.min(x, window.innerWidth - popoverRect.width - padding));
    if (y + popoverRect.height > window.innerHeight - padding) {
      y = triggerRect.top - popoverRect.height - OFFSET;
    }

    setCoords({ x, y });
  }, []);

  const show = () => {
    clearTimeout(hideTimeoutRef.current);
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 200);
  };

  const hide = () => {
    clearTimeout(showTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 150);
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, updatePosition]);

  useEffect(() => {
    return () => {
      clearTimeout(showTimeoutRef.current);
      clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    onClickManage();
  };

  const popover = isVisible && members.length > 0 && (
    <div
      ref={popoverRef}
      className={cn(
        'fixed z-[150] w-64',
        'bg-[var(--glass-strong)]',
        'backdrop-blur-[20px]',
        'border border-[var(--glass-border)]',
        'rounded-xl',
        'shadow-xl',
        'animate-in fade-in zoom-in-95 duration-150',
        'overflow-hidden'
      )}
      style={{ left: coords.x, top: coords.y }}
      onMouseEnter={() => clearTimeout(hideTimeoutRef.current)}
      onMouseLeave={hide}
    >
      <div className="px-3 py-2 border-b border-white/10">
        <p className="text-xs font-semibold text-foreground">{team.name}</p>
        <p className="text-[11px] text-muted-foreground">{memberCount} members</p>
      </div>
      <div className="px-3 py-2 space-y-1.5">
        {members.slice(0, MAX_PREVIEW).map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-2">
            <span className="text-[12px] text-foreground truncate">
              {m.firstName} {m.lastName}
            </span>
            <GlassBadge variant="info" size="sm">
              {m.resourceLevelCode}
            </GlassBadge>
          </div>
        ))}
        {members.length > MAX_PREVIEW && (
          <p className="text-[11px] text-muted-foreground">
            +{members.length - MAX_PREVIEW} more
          </p>
        )}
      </div>
      <button
        onClick={handleClick}
        className="w-full px-3 py-2 border-t border-white/10 text-[11px] font-medium text-accent-blue hover:bg-white/5 transition-colors text-left"
      >
        Click to manage roster
      </button>
    </div>
  );

  return (
    <>
      <div
        ref={triggerRef}
        className="inline-flex items-center gap-1 cursor-pointer"
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={handleClick}
      >
        <Users className="w-4 h-4 text-[var(--muted)]" />
        <span className="font-medium text-accent-violet">{memberCount}</span>
      </div>
      {isVisible && createPortal(popover, document.body)}
    </>
  );
};

export default TeamMembersPopover;
